import { ref, onMounted, onUnmounted } from 'vue'
import { SITE_LOCALES, getLocaleFromPath } from '~/utils/locales'

interface BotDetectionData {
  timeBeforeSubmit: number | null
  pagesViewed: number
  scrollEvents: number
  mouseEvents: number
  resolution: string
  language: string
  isTor: boolean
  timestamp: number
}

const botData = ref<BotDetectionData>({
  timeBeforeSubmit: null,
  pagesViewed: 0,
  scrollEvents: 0,
  mouseEvents: 0,
  resolution: '',
  language: '',
  isTor: false,
  timestamp: Date.now()
})

const pageLoadTime = ref<number>(Date.now())
const scrollCount = ref<number>(0)
const mouseCount = ref<number>(0)

// Track scroll events
const handleScroll = () => {
  scrollCount.value++
  botData.value.scrollEvents = scrollCount.value
}

// Track mouse events
const handleMouseMove = () => {
  mouseCount.value++
  botData.value.mouseEvents = mouseCount.value
}

// Detect Tor
const detectTor = (): boolean => {
  try {
    if (typeof window === 'undefined') return false
    
    // Check for Tor browser fingerprint
    // Tor browser typically has no plugins and specific user agent patterns
    const isTorBrowser = navigator.userAgent.includes('Tor') || 
                         (navigator as any).plugins?.length === 0 ||
                         (window as any).navigator?.plugins?.length === 0
    
    // Additional check: Tor often has specific screen dimensions
    const screenSize = `${window.screen.width}×${window.screen.height}`
    const isTorScreenSize = screenSize === '1000×1000' || screenSize === '1280×800'
    
    return isTorBrowser || isTorScreenSize
  } catch {
    return false
  }
}

// Get resolution
const getResolution = (): string => {
  if (typeof window === 'undefined') return ''
  return `${window.screen.width}×${window.screen.height}`
}

// Get language
const getLanguage = (): string => {
  if (typeof window === 'undefined') return ''
  return navigator.language || (navigator as any).userLanguage || ''
}

// Get pages viewed from sessionStorage
const getPagesViewed = (): number => {
  if (typeof window === 'undefined') return 0
  const count = sessionStorage.getItem('pages_viewed')
  return count ? parseInt(count, 10) : 0
}

// Increment pages viewed
const incrementPagesViewed = () => {
  if (typeof window === 'undefined') return
  const current = getPagesViewed()
  sessionStorage.setItem('pages_viewed', String(current + 1))
  botData.value.pagesViewed = current + 1
}

// Initialize bot detection
export function useBehavioralBotDetection() {
  const initialize = async () => {
    if (typeof window === 'undefined') return

    // Set initial values
    botData.value.resolution = getResolution()
    botData.value.language = getLanguage()
    botData.value.pagesViewed = getPagesViewed()
    botData.value.isTor = detectTor()
    pageLoadTime.value = Date.now()

    // Increment pages viewed
    incrementPagesViewed()

    // Add event listeners
    window.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('mousemove', handleMouseMove, { passive: true })
    window.addEventListener('click', handleMouseMove, { passive: true })
  }

  // Get bot detection data for submission
  const getBotData = (action: string): BotDetectionData => {
    const timeBeforeSubmit = Date.now() - pageLoadTime.value
    const timeInSeconds = timeBeforeSubmit / 1000

    return {
      ...botData.value,
      timeBeforeSubmit: timeInSeconds,
      timestamp: Date.now()
    }
  }

  // Check if bot-like behavior is detected (2+ criteria)
  const checkBotCriteria = (data: BotDetectionData): { isBot: boolean; criteria: string[] } => {
    const criteria: string[] = []

    // Time before submit < 5 seconds
    if (data.timeBeforeSubmit !== null && data.timeBeforeSubmit < 5) {
      criteria.push('time_before_submit')
    }

    // Pages viewed > 10
    if (data.pagesViewed > 10) {
      criteria.push('pages_viewed')
    }

    // Scroll = 0
    if (data.scrollEvents === 0) {
      criteria.push('no_scroll')
    }

    // Mouse events = 0
    if (data.mouseEvents === 0) {
      criteria.push('no_mouse_events')
    }

    // Resolution is fixed (1900×1000)
    if (data.resolution === '1900×1000') {
      criteria.push('fixed_resolution')
    }

    // Language inconsistent (check if language doesn't match URL or common patterns)
    const urlLang = getLocaleFromPath(window.location.pathname)
    const browserLang = data.language.split('-')[0].toLowerCase()
    if (urlLang !== browserLang && !SITE_LOCALES.some(locale => locale.code === browserLang)) {
      criteria.push('inconsistent_language')
    }

    // Tor detected
    if (data.isTor) {
      criteria.push('tor_detected')
    }

    return {
      isBot: criteria.length >= 2,
      criteria
    }
  }

  // Cleanup
  const cleanup = () => {
    if (typeof window === 'undefined') return
    window.removeEventListener('scroll', handleScroll)
    window.removeEventListener('mousemove', handleMouseMove)
    window.removeEventListener('click', handleMouseMove)
  }

  return {
    initialize,
    getBotData,
    checkBotCriteria,
    cleanup,
    botData
  }
}
